import api from '../../utils/api';
import { updateEmployee, deleteEmployeeById } from '../../utils/api';

export const getEmployees = async () => {
  return api.get('/master-admin/employees');
};

export type EmployeeFormFields = {
  name: string;
  email: string;
  password?: string;
  role: string;
  department: string;
  phone: string;
  qualification?: string;
  created_by: string;
};

export type EmployeeUploadOptions = {
  profileImageFile?: File | null;
  removeProfileImage?: boolean;
};

export type TeamMemberAccessDetails = {
  member: {
    id: string;
    name: string;
    email: string;
    role: string;
    department?: string;
    phone?: string;
    qualification?: string | null;
    profile_image?: string | null;
    status?: string;
    last_login_at?: string | null;
    createdAt?: string;
  };
  authActivity: {
    isLoggedIn: boolean;
    activeSessions: number;
    lastLogin: string | null;
    lastLogout: string | null;
    distinctDeviceCount: number;
    distinctOperatorLabels: string[];
    recentSessions: Array<{
      id: string;
      userType: string;
      sessionId: string;
      deviceId: string;
      platform: string;
      operatorLabel?: string | null;
      ipAddress?: string | null;
      userAgent?: string | null;
      browser?: string | null;
      os?: string | null;
      deviceLabel?: string | null;
      deviceModel?: string | null;
      deviceCode?: string | null;
      status: string;
      loginAt: string;
      logoutAt?: string | null;
      lastSeenAt?: string | null;
    }>;
  };
  fcmTokens: Array<{
    id: number;
    userType?: string | null;
    fcmToken: string;
    deviceId: string;
    platform: string;
    ipAddress?: string | null;
    userAgent?: string | null;
    browser?: string | null;
    os?: string | null;
    isActive: boolean;
    lastUsedAt?: string | null;
  }>;
  activityLogs: Array<{
    id: string;
    action: string;
    category?: string | null;
    pageName?: string | null;
    performedAction?: string | null;
    operatorLabel?: string | null;
    path?: string | null;
    summary?: string | null;
    meta?: Record<string, unknown> | null;
    deviceId?: string | null;
    deviceLabel?: string | null;
    deviceModel?: string | null;
    deviceCode?: string | null;
    ipAddress?: string | null;
    browser?: string | null;
    os?: string | null;
    createdAt: string;
  }>;
  notifications: Array<{
    id: number | string;
    title: string;
    body: string;
    type: string;
    isRead?: boolean;
    createdAt: string;
  }>;
};

export const fetchTeamMemberAccessDetails = async (
  id: string
): Promise<{ success: boolean; data?: TeamMemberAccessDetails; message?: string }> => {
  const response = await api.get(`/master-admin/employees/${id}/access-details`);
  return response.data;
};

export const recordAdminClientActivity = async (payload: {
  action?: string;
  path?: string;
  pageName?: string;
  performedAction?: string;
  summary?: string;
  category?: string;
}) => {
  const sessionId = localStorage.getItem('adminSessionId') || undefined;
  const operatorLabel = localStorage.getItem('adminOperatorLabel') || undefined;
  let deviceIdentity: {
    deviceId: string;
    deviceLabel: string;
    deviceModel: string | null;
    deviceCode: string;
  } | null = null;
  try {
    const { getAdminDeviceIdentity } = await import('../../utils/adminDeviceIdentity');
    deviceIdentity = await getAdminDeviceIdentity();
  } catch {
    /* best-effort */
  }
  return api.post('/master-admin/employees/activity', {
    ...payload,
    sessionId,
    deviceId: deviceIdentity?.deviceId || localStorage.getItem('vedika_admin_fcm_device_id') || undefined,
    operatorLabel,
    deviceLabel: deviceIdentity?.deviceLabel,
    deviceModel: deviceIdentity?.deviceModel || undefined,
    deviceCode: deviceIdentity?.deviceCode,
  });
};

const appendEmployeeFormData = (
  fd: FormData,
  data: EmployeeFormFields,
  options?: EmployeeUploadOptions
) => {
  fd.append('name', data.name);
  fd.append('email', data.email);
  if (data.password) {
    fd.append('password', data.password);
  }
  fd.append('role', data.role);
  fd.append('department', data.department);
  fd.append('phone', data.phone);
  fd.append('created_by', data.created_by);
  if (data.qualification?.trim()) {
    fd.append('qualification', data.qualification.trim());
  }
  if (options?.profileImageFile) {
    fd.append('profileImage', options.profileImageFile);
  }
  if (options?.removeProfileImage) {
    fd.append('removeProfileImage', 'true');
  }
};

export const registerEmployeeService = async (
  data: EmployeeFormFields & { password: string },
  options?: EmployeeUploadOptions
) => {
  const fd = new FormData();
  appendEmployeeFormData(fd, data, options);
  return api.post('/master-admin/register', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

export const updateEmployeeService = async (
  id: string,
  data: EmployeeFormFields,
  options?: EmployeeUploadOptions
) => {
  const fd = new FormData();
  appendEmployeeFormData(fd, data, options);
  return updateEmployee(id, fd);
};

export const deleteEmployeeService = async (id: string) => {
  return deleteEmployeeById(id);
};

export const getMyProfile = async (): Promise<{ success?: boolean; user: any }> => {
  const response = await api.get('/master-admin/profile');
  return response.data;
};

export const updateMyProfileService = async (
  data: EmployeeFormFields & { password?: string },
  options?: EmployeeUploadOptions
) => {
  const fd = new FormData();
  fd.append('name', data.name);
  fd.append('email', data.email);
  fd.append('department', data.department);
  fd.append('phone', data.phone);
  if (data.qualification?.trim()) {
    fd.append('qualification', data.qualification.trim());
  }
  if (data.password?.trim()) {
    fd.append('password', data.password.trim());
  }
  if (options?.profileImageFile) {
    fd.append('profileImage', options.profileImageFile);
  }
  if (options?.removeProfileImage) {
    fd.append('removeProfileImage', 'true');
  }
  return api.put('/master-admin/profile', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};
